import { ActivatedRouteSnapshot } from '@angular/router';

/**
 * Full resolved path + sorted query string for a route snapshot. Used as the
 * identity for both open tabs and the reuse-strategy's cache, since
 * `route.routeConfig` is shared across different `:id` values (e.g.
 * `/dashboard/:id`) and can't distinguish two tabs on the same route config.
 */
export function buildRouteCacheKey(route: ActivatedRouteSnapshot): string {
  const path =
    '/' +
    route.pathFromRoot
      .flatMap((r) => r.url.map((segment) => segment.path))
      .filter(Boolean)
      .join('/');

  const keys = [...route.queryParamMap.keys].sort((a, b) => a.localeCompare(b));
  const query = keys.length
    ? '?' +
      keys.map((key) => `${key}=${route.queryParamMap.get(key)}`).join('&')
    : '';

  return path + query;
}

/**
 * True only for a snapshot whose route config actually renders a component.
 * Many routes (e.g. /production, /approvisionnements) use a component-less
 * parent purely to host guards/data, with the real page one level down on an
 * empty-path child — that child resolves to the exact same `buildRouteCacheKey`
 * as its parent (a pathless segment contributes nothing to the URL). Angular
 * calls `shouldAttach`/`retrieve` on every node while building the future
 * route tree, not just component-bearing ones, so without this check the
 * parent and child would collide on one cache entry and the same detached
 * handle would get grafted in at both positions — a node attached as its own
 * child, corrupting the route tree.
 */
export function routeHasComponent(route: ActivatedRouteSnapshot): boolean {
  return !!(route.routeConfig?.component || route.routeConfig?.loadComponent);
}
